import { Injectable } from '@angular/core';
import {
    getProjectOrderNumber,
    locationIdentifiersKey,
    parentLocationIdentifierKey,
    ProjectDto,
} from '@pf/platform-wms/domain';
import { asArraySafe, asNumberSafe, isValue } from '@pf/shared-utility';
import { sumBy, uniqBy } from 'lodash-es';
import { CpwProjectType } from '../models/cpw-project-types';
import { ProjectSummary } from '../models/project-summary.model';
import { SlotSummary } from '../models/slot-summary.model';
import { ZoneSummary } from '../models/zone-summary.model';
import {
    EquipmentTypeFragment,
    ItemCatalogCategory,
    PickFragment,
    PickLocationFragment,
    ProjectLineItemFragment,
} from '@control-tower/platform-wms';
import { isHandlingLtl } from '../models/cpw-equipment-types';

const UNIFORMS_CATEGORY_NAME = 'Uniforms';
const UNIFORMS_CATEGORY_CODE = 'UNI';

@Injectable()
export class ProjectSummaryFactory {
    create(project: ProjectDto): ProjectSummary {
        const lineItems = asArraySafe(project.lineItems);
        const pickListIds = lineItems.reduce((pickListIds, lineItem) => {
            if (!lineItem.pick?.pickListId) return pickListIds;
            pickListIds.add(lineItem.pick.pickListId);
            return pickListIds;
        }, new Set<string>());
        const picks = lineItems
            .map(lineItem => lineItem.pick)
            .filter(isValue) as PickFragment[];
        const pickLocations = picks.flatMap(p => asArraySafe(p.pickLocations));

        const totalQuantityToPick = this.getTotalQuantity(lineItems);
        const { zones, slots } = this.getZoneAndSlotSummaries(pickLocations);
        const equipmentTypes = this.getEquipmentTypes(pickLocations);
        const isUniforms = this.isUniformsOnlyDemand(lineItems);

        const orderNumber = getProjectOrderNumber(project);
        return {
            projectId: project.id,
            projectName: `${orderNumber} | ${project.projectType!.name}`,
            projectNumber: orderNumber,
            projectTypeId: project.projectType!.id as CpwProjectType,
            createdDate: new Date(project.createdDate as string),
            endDate: new Date(project.endDate as string),
            pickListIds: [...pickListIds.values()],
            totalPicks: pickLocations.length,
            zones,
            slots,
            totalQuantityToPick,
            equipmentTypes,
            isLtl: equipmentTypes.some(eq => isHandlingLtl(eq)),
            isUniforms,
        } as ProjectSummary;
    }

    private getTotalQuantity(lineItems: ProjectLineItemFragment[]) {
        return sumBy(lineItems, x => asNumberSafe(x.quantity));
    }

    private getEquipmentTypes(
        pickLocations: PickLocationFragment[]
    ): EquipmentTypeFragment[] {
        const equipmentTypes = pickLocations
            .map(
                pl =>
                    pl.inventoryBundle?.container?.equipment?.equipmentType ||
                    pl.inventoryBundle?.container?.slot?.equipmentType
            )
            .filter(isValue);
        return uniqBy(
            equipmentTypes,
            x => x!.id as string
        ) as EquipmentTypeFragment[];
    }

    private getZoneAndSlotSummaries(pickLocations: PickLocationFragment[]) {
        const zones: Record<string, ZoneSummary> = {};
        const slots: Record<string, SlotSummary> = {};
        pickLocations.forEach(pickLocation => {
            const slot = pickLocation.inventoryBundle?.container?.slot;
            if (!slot?.id) return;

            if (!slots[slot.id]) {
                slots[slot.id] = {
                    id: slot.id,
                    barcode: slot.barcode || '',
                    equipmentType: slot.equipmentType || null,
                };
            }
            const zone = slot.zone;

            if (!zone?.id) return;

            if (!zones[zone.id]) {
                zones[zone.id] = {
                    id: zone.id,
                    name: zone.name || '',
                    locationIdentifier: locationIdentifiersKey(
                        zone.locationIdentifiers
                    ),
                    parentLocationIdentifier: parentLocationIdentifierKey(
                        zone.locationIdentifiers
                    ),
                    barcode: zone.barcode || '',
                    totalQuantity: 0,
                };
            }

            zones[zone.id].totalQuantity += asNumberSafe(
                pickLocation.quantityNeeded
            );
        });

        return {
            zones: Object.values(zones).sort((a, b) =>
                a.barcode.localeCompare(b.barcode)
            ),
            slots: Object.values(slots).sort((a, b) =>
                a.barcode.localeCompare(b.barcode)
            ),
        };
    }

    private isUniformsOnlyDemand(
        lineItems: ProjectLineItemFragment[]
    ): boolean {
        if (!lineItems.length) {
            return false;
        }

        // Check if every line item belongs to the Uniforms category tree
        return lineItems.every(lineItem =>
            this.isItemInUniformsCategory(lineItem)
        );
    }

    private isItemInUniformsCategory(
        lineItem: ProjectLineItemFragment
    ): boolean {
        const item = lineItem.item;
        if (!item?.assignedCategories?.length) {
            return false;
        }

        // Check if any assigned category is in the Uniforms tree
        return item.assignedCategories.some(category =>
            this.isUniformsCategory(category)
        );
    }

    private isUniformsCategory(category: ItemCatalogCategory): boolean {
        // Check by category name
        if (category.name === UNIFORMS_CATEGORY_NAME) {
            return true;
        }

        // Check by category code
        if (category.code === UNIFORMS_CATEGORY_CODE) {
            return true;
        }

        // Check if it's a sub-category (breadcrumbs contain "Uniforms")
        return !!category.categoryBreadCrumbs?.includes(UNIFORMS_CATEGORY_NAME);
    }
}
